import React, { useEffect, useRef } from 'react';
import { resolvePublicMediaSrc } from '../utils/i3Assets';
import '../styles/I3MediaViewer.css';

type MediaType = 'image' | 'video';

type I3MediaViewerProps = {
  isOpen: boolean;
  onClose: () => void;
  type: MediaType;
  /** For image: src URL. For video: embed URL (e.g. YouTube) or src for <video> */
  src: string;
  title?: string;
};

const I3MediaViewer: React.FC<I3MediaViewerProps> = ({ isOpen, onClose, type, src, title }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  if (!isOpen) return null;

  const hasSrc = typeof src === 'string' && src.trim().length > 0;
  const resolvedSrc = hasSrc ? resolvePublicMediaSrc(src) : '';
  const isYouTube = type === 'video' && hasSrc && src.includes('youtube');
  const embedUrl = isYouTube && src.includes('watch') ? src.replace('watch?v=', 'embed/') : src;

  return (
    <div className="i3-media-viewer-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={title || 'Media viewer'}>
      <div
        className="i3-media-viewer-container"
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="i3-media-viewer-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        {hasSrc && (
          <button type="button" className="i3-media-viewer-fullscreen" onClick={handleFullscreen} aria-label="Fullscreen">
            ⛶
          </button>
        )}
        {title && <p className="i3-media-viewer-title">{title}</p>}
        <div className="i3-media-viewer-content">
          {!hasSrc ? (
            <p className="i3-media-viewer-placeholder">Add video or image URL in PAST_EVENTS</p>
          ) : type === 'image' ? (
            <img src={resolvedSrc} alt={title || ''} className="i3-media-viewer-img" />
          ) : isYouTube || embedUrl.startsWith('http') ? (
            <iframe
              src={embedUrl}
              title={title || 'Video'}
              className="i3-media-viewer-iframe"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video src={resolvedSrc} controls autoPlay className="i3-media-viewer-video" />
          )}
        </div>
      </div>
    </div>
  );
};

export default I3MediaViewer;
